import Box from '@mui/material/Box';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MFTextField, MFSubmitButton } from '../../lib/formik';
import { useDispatch, useSelector } from 'react-redux';
import { loginWithOtp } from '../../redux-store/actions/auth';
import { useHistory, useLocation } from 'react-router';
import { nonDigitRegex } from '../../utils/regex';
import { LoginWithOtpResponseBody } from '../../redux-store/types/api-types';
import { CountryCodesDropDown, LoginWrapper } from '../commonComponents';
import { getCountryCodes, preventSpecialCharacters } from '../../utils/utilityFunctions';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InvestorSignUp from './InvestorLogin/InvestorSignUp';
import { RootStateType } from '../../redux-store/reducers';

interface Values {
  mobile: string;
  countryCode: string;
  countryNameAndCode: string;
}

const validationSchema = yup.object().shape({
  mobile: yup.string().test('mobile', (value, context) => {
    const { createError, path, parent } = context;
    const codesList = getCountryCodes()
      .map((codes) => {
        if (codes.label === parent.countryNameAndCode) {
          return codes.countryCode;
        }
      })
      .filter((ele) => ele)
      .toString();
    if (!value) {
      return createError({
        message: 'Mobile number is required',
        path,
      });
    }
    // if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
    //   return createError({
    //     message: 'Invalid Mobile number',
    //     path,
    //   });
    // }
    if (
      !isValidPhoneNumber(
        parent?.countryNameAndCode?.split(':')[1].trim() + value,
        codesList as CountryCode
      )
    ) {
      return createError({
        message: 'Invalid Mobile number',
        path,
      });
    }
    return true;
  }),
  countryNameAndCode: yup
    .string()
    .nullable()
    .test('countryNameAndCode', (value, context) => {
      const { createError, path, parent } = context;
      const codesList = getCountryCodes()
        .map((codes) => {
          if (codes.label === value) {
            return codes.countryCode;
          }
        })
        .filter((ele) => ele)
        .toString();
      if (!value) {
        return createError({
          message: 'Country Code is required',
          path,
        });
      }
      if (
        parent.mobile &&
        !isValidPhoneNumber(value?.split(':')[1].trim() + parent.mobile, codesList as CountryCode)
      ) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }

      return true;
    }),
});

export function MobileLogin(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const loginType = location.pathname.includes('investor') ? 'investor' : '';
  const token = useSelector((store: RootStateType) => store?.preventLogoutReducer?.token) || '';
  const arn_link = new URLSearchParams(window.location.search).get('arn_link') || '';

  const onSubmit = async (values: Values) => {
    try {
      const { mobile = '', countryCode = '+91', countryNameAndCode = 'India: +91' } = values;
      const res = (await dispatch(
        loginWithOtp({
          phone: mobile.trim(),
          countryCode: countryNameAndCode?.split(':')[1].trim(),
          ...(loginType && { role: loginType }),
          ...(token && { token: token?.toString() }),
          ...(arn_link && { arn_link: arn_link?.toString() }),
        })
      )) as unknown as LoginWithOtpResponseBody;
      history.push('verify-otp', {
        mobile,
        countryCode: countryNameAndCode?.split(':')[1].trim(),
        ...(res || {}),
        ...(loginType && { role: loginType }),
        ...(token && { token: token.toString() }),
        ...(arn_link && { arn_link: arn_link.toString() }),
      });
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    mobile: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
      {({ handleSubmit, values }) => (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '.MuiInputLabel-root': {
              fontFamily: 'Poppins, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              color: '#4a5568',
            },
            '.MuiOutlinedInput-root': {
              borderRadius: '11px',
              backgroundColor: '#f8faff',
              fontSize: 15,
              '& fieldset': {
                borderColor: '#e2e8f0',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: '1.5px',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: '0 0 0 3px rgba(59,107,214,0.10)',
              },
            },
            '.MuiFormControl-root': {
              position: 'relative',
              mb: '28px',
            },
            '.MuiFormHelperText-root': {
              position: 'absolute',
              bottom: '-18px',
              margin: 0,
              fontSize: 12,
            },
          }}>
          <MFTextField
            label="Mobile Number"
            name="mobile"
            placeholder="Enter Mobile number"
            onKeyDown={(e) => preventSpecialCharacters(e)}
            startAdornment={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  // ✅ Remove all inner borders from nested select
                  '.MuiOutlinedInput-notchedOutline': { display: 'none' },
                  '.MuiOutlinedInput-root': {
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    pr: 0,
                  },
                  '.MuiSelect-select': {
                    py: '0 !important',
                    pl: '0 !important',
                    fontSize: 14,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 500,
                  },
                }}>
                <CountryCodesDropDown
                  name={'countryNameAndCode'}
                  value={values.countryNameAndCode}
                />
                {/* ✅ ONE divider only — removed the extra mr/ml that caused gap+line */}
                <Box sx={{ width: '1px', height: '20px', bgcolor: '#e2e8f0' }} />
              </Box>
            }
            autoFocus
            type="number"
            regexForFilterValue={nonDigitRegex}
            sx={{
              '.MuiOutlinedInput-root': {
                borderRadius: '11px',
                backgroundColor: '#f8faff',
                pl: 1,
                // ✅ single border via fieldset only, no double lines
                '& fieldset': {
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': { borderColor: '#a0aec0' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 3px rgba(59,107,214,0.10)',
                },
              },
            }}
          />

          <MFSubmitButton label="Get OTP" />

          {loginType === 'investor' && (
            <Typography
              sx={{ textAlign: 'center', fontSize: 14, color: 'text.secondary', mt: '16px' }}>
              If you are a new investor,&nbsp;
              <Button
                component={RouterLink}
                to={{
                  pathname: '/investor-signup',
                  search:
                    arn_link && token
                      ? `?token=${token}&arn_link=${arn_link}`
                      : token
                      ? `?token=${token}`
                      : arn_link
                      ? `?arn_link=${arn_link}`
                      : '',
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: 'none',
                  color: 'primary.main',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}>
                click here
              </Button>
            </Typography>
          )}
        </Box>
      )}
    </Formik>
  );
}

export default function MarcellusLogin(): JSX.Element {
  return (
    <>
      <LoginWrapper
        investorLoginForm={<MobileLogin />}
        investorSignupForm={<InvestorSignUp />}
        distributorForm={<MobileLogin />}
      />
    </>
  );
}
